import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Eye, Heart, Layers, RefreshCw, FileCheck } from 'lucide-react';
import { PageSEO } from '@/components/SEO';

const prosess = [
  {
    icon: Eye,
    title: 'Kartlegging',
    description: 'Vi lærer om din bedrift, verdier og målgruppe for å forstå hva logoen skal kommunisere.',
  },
  {
    icon: Palette,
    title: 'Konseptutvikling',
    description: 'Vi utvikler flere unike konsepter basert på brief og research.',
  },
  {
    icon: RefreshCw,
    title: 'Tilbakemelding',
    description: 'Du gir tilbakemelding og vi justerer designet til det er perfekt.',
  },
  {
    icon: FileCheck,
    title: 'Levering',
    description: 'Du mottar logoen i alle nødvendige formater for print og digital bruk.',
  },
];

const inkluderer = [
  'Primærlogo i farger',
  'Logo i sort/hvitt',
  'Logoikon for sosiale medier',
  'Alle filformater (AI, EPS, PNG, JPG, SVG)',
  'Brandguide med farger og fonter',
  'Ubegrenset revisjon til du er fornøyd',
];

export default function Logodesign() {
  return (
    <div>
      <PageSEO title="Logodesign – Profesjonell merkevarebygging i Norge" path="/tjenester/logodesign" description="Vi designer unike logoer for bedrifter over hele Norge. Bygg en sterk merkevare med profesjonelt design. Brandguide og alle filformater inkludert." />
      {/* Hero */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="logodesign-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Tjeneste
              </p>
              <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
                Logo som bygger merkevare
              </h1>
              <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
                Vi designer profesjonelle logoer som bygger tillit, styrker merkevaren din og gir bedriften et tydelig og gjenkjennelig uttrykk.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group mt-8"
                data-testid="logodesign-cta"
              >
                Start ditt logo-prosjekt
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            <div className="relative group rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-accent/20 rounded-3xl" />
              <img
                src="https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80"
                alt="Logodesign og merkevarebygging – Side Kraft"
                className="relative z-10 w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Prosess */}
      <section className="py-24 md:py-32" data-testid="logodesign-prosess">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Vår prosess
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight">
              Fra idé til ferdig logo
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {prosess.map((steg, index) => (
              <div
                key={index}
                className="p-8 border border-border rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`prosess-${index}`}
              >
                <span className="font-syne font-bold text-4xl text-accent/30 mb-4 block">0{index + 1}</span>
                <steg.icon className="w-8 h-8 text-accent mb-4" />
                <h3 className="font-syne font-bold text-lg text-primary mb-2">
                  {steg.title}
                </h3>
                <p className="font-manrope text-sm text-muted-foreground leading-relaxed">
                  {steg.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hva du får */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="logodesign-inkluderer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Leveranse
              </p>
              <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight mb-8">
                Hva du får
              </h2>
              <ul className="space-y-4">
                {inkluderer.map((item, index) => (
                  <li key={index} className="flex items-center gap-3" data-testid={`inkluderer-${index}`}>
                    <div className="w-2 h-2 bg-accent flex-shrink-0" />
                    <span className="font-manrope text-primary-foreground/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative group rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5">
              <img
                src="https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&q=80"
                alt="Logo eksempler og visuell identitet"
                className="w-full h-[400px] object-cover opacity-80 transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-muted" data-testid="logodesign-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
            Klar for en ny logo?
          </h2>
          <p className="font-manrope text-muted-foreground max-w-xl mx-auto mb-10">
            Ta kontakt for en uforpliktende samtale om din nye logo og visuelle identitet.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
            data-testid="logodesign-bottom-cta"
          >
            Kontakt oss
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
